import { cloneDeep } from 'lodash-es';

const TAG = {
	POINT:       'point',
	GAME_START:  'game_start',
	GAME_END:    'game_end',
	SET_START:   'set_start',
	SET_END:     'set_end',
	MATCH_START: 'match_start',
	MATCH_END:   'match_end',
	SCORE_SET:   'score_set',
};

const bootstrapSet = (event) => {
	return event.participants.map((participant) => {
		return {
			playerId: participant.participatable.id,
			points:   0,
			games:    0,
		};
	});
};

const validateScoreSet = (scoreSet) => {
	if (!scoreSet) return false;
	if (!Array.isArray(scoreSet.game)) return false;
	if (!Array.isArray(scoreSet.sets)) return false;
	return true;
};
// {
//    "game": [40, "AD"],
//    "sets": [[6, 4], [0, 0]]
// }

const getScore = ({ event, tags, timestamp }) => {
	if (event.participants.length !== 2) {
		throw new Error(`Invalid tennis event with ${event.participants.length} participants.`);
	}

	const sets = [bootstrapSet(event)];
	let processedFirstSetStart = false;

	const filteredTags = tags.filter((currentTag) => Object.values(TAG).includes(currentTag.name) && currentTag.timestamp < timestamp);
	filteredTags.sort((a, b) => a.timestamp - b.timestamp);

	for (const tag of filteredTags) {
		// point
		if (tag.playerId && tag.name === TAG.POINT) {
			sets[sets.length - 1].find((setParticipant) => setParticipant.playerId === tag.playerId).points += 1;
		}
		// game_start
		if (tag.name === TAG.GAME_START) {
			let hasChanges = false;
			for (const participant of sets[sets.length - 1]) {
				if (participant.points !== 0) hasChanges = true;
				participant.points = 0;
			}
			if (!hasChanges) continue;
		}
		// game_end
		if (tag.playerId && tag.name === TAG.GAME_END) {
			sets[sets.length - 1].find((setParticipant) => setParticipant.playerId === tag.playerId).games += 1;
			for (const participant of sets[sets.length - 1]) {
				participant.points = 0;
			}
		}
		// set_start
		if (tag.name === TAG.SET_START) {
			if (processedFirstSetStart) {
				sets.push(bootstrapSet(event));
			} else {
				processedFirstSetStart = true;
			}
		}
	}

	return { sets, currentSet: sets[sets.length - 1] };
};

const getScoreZones = ({ event, tags }) => {
	if (event.participants.length !== 2) {
		throw new Error(`Invalid tennis event with ${event.participants.length} participants.`);
	}

	const zones = [{ start: 0, end: Number.POSITIVE_INFINITY, sets: [bootstrapSet(event)] }];

	const filteredTags = tags.filter((currentTag) => Object.values(TAG).includes(currentTag.name));
	filteredTags.sort((a, b) => a.timestamp - b.timestamp);

	const scoreSetTags = filteredTags.filter((currentTag) => {
		if (currentTag.name !== TAG.SCORE_SET) return false;
		return validateScoreSet(currentTag.data);
	});

	if (scoreSetTags.length > 0) {
		for (const tag of scoreSetTags) {
			zones[zones.length - 1].end = tag.timestamp;
			const sets = [];

			for (const [setIndex, set] of tag.data.sets.entries()) {
				const currentSet = [];
				if (set.length !== event.participants.length) {
					throw new Error(
						`Invalid score_set tag with mismatched participant count (${set.entries.length} set entries, ${event.participants.length} participants).`
					);
				}
				for (const [i, entry] of set.entries()) {
					currentSet.push({
						playerId: event.participants[i].participatable.id,
						games:    entry,
						points:   setIndex === tag.data.sets.length - 1 ? tag.data.game[i] ?? 0 : 0,
					});
				}
				sets.push(currentSet);
			}

			if (sets.length === 0) sets.push(bootstrapSet(event));

			const zone = { start: tag.timestamp, end: Number.POSITIVE_INFINITY, sets };
			zones.push(zone);
		}

		return zones;
	}

	const sets = [bootstrapSet(event)];
	let processedFirstSetStart = false;

	for (const tag of filteredTags) {
		if (tag.playerId && tag.name === TAG.POINT) {
			// point
			sets[sets.length - 1].find((currentSet) => currentSet.playerId === tag.playerId).points += 1;
		} else if (tag.name === TAG.GAME_START) {
			// game_start
			let hasChanges = false;
			for (const participant of sets[sets.length - 1]) {
				if (participant.points !== 0) hasChanges = true;
				participant.points = 0;
			}
			if (!hasChanges) continue;
		} else if (tag.playerId && tag.name === TAG.GAME_END) {
			// game_end
			sets[sets.length - 1].find((currentSet) => currentSet.playerId === tag.playerId).games += 1;
			for (const participant of sets[sets.length - 1]) {
				participant.points = 0;
			}
		} else if (tag.name === TAG.SET_START) {
			// set_start
			if (processedFirstSetStart) {
				sets.push(bootstrapSet(event));
			} else {
				processedFirstSetStart = true;
				continue;
			}
		} else {
			continue;
		}

		zones[zones.length - 1].end = tag.timestamp;
		zones.push({
			start: tag.timestamp,
			end:   Number.POSITIVE_INFINITY,
			sets:  cloneDeep(sets),
		});
	}

	return zones;
};

export { getScore, getScoreZones, TAG };
